import type { CSSProperties, ReactElement } from "react";

export type FlagCode = "gb" | "fr" | "ma" | "es" | "it" | "de" | "nl";

type Props = {
  size: number;
  title?: string;
};

const baseStyle = (size: number): CSSProperties => ({
  width: size,
  height: size,
  display: "block",
});

function GbFlag({ size, title }: Props) {
  return (
    <svg
      viewBox="0 0 60 60"
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid slice"
      role={title ? "img" : "presentation"}
      aria-label={title}
      style={baseStyle(size)}
    >
      <rect width="60" height="60" fill="#012169" />
      <path d="M0 0 L60 60 M60 0 L0 60" stroke="#fff" strokeWidth="12" />
      <path d="M0 0 L60 60" stroke="#C8102E" strokeWidth="6" />
      <path d="M60 0 L0 60" stroke="#C8102E" strokeWidth="6" />
      <path d="M30 0 V60 M0 30 H60" stroke="#fff" strokeWidth="20" />
      <path d="M30 0 V60 M0 30 H60" stroke="#C8102E" strokeWidth="10" />
    </svg>
  );
}

function FrFlag({ size, title }: Props) {
  return (
    <svg
      viewBox="0 0 60 60"
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid slice"
      role={title ? "img" : "presentation"}
      aria-label={title}
      style={baseStyle(size)}
    >
      <rect width="20" height="60" fill="#0055A4" />
      <rect x="20" width="20" height="60" fill="#fff" />
      <rect x="40" width="20" height="60" fill="#EF4135" />
    </svg>
  );
}

function MaFlag({ size, title }: Props) {
  return (
    <svg
      viewBox="0 0 60 60"
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid slice"
      role={title ? "img" : "presentation"}
      aria-label={title}
      style={baseStyle(size)}
    >
      <rect width="60" height="60" fill="#C1272D" />
      <path
        d="M30 18 l3.708 11.41 12 0 -9.708 7.052 3.708 11.41 -9.708 -7.053 -9.708 7.053 3.708 -11.41 -9.708 -7.052 12 0 z"
        fill="none"
        stroke="#006233"
        strokeWidth="1.6"
        strokeLinejoin="miter"
      />
    </svg>
  );
}

function EsFlag({ size, title }: Props) {
  return (
    <svg
      viewBox="0 0 60 60"
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid slice"
      role={title ? "img" : "presentation"}
      aria-label={title}
      style={baseStyle(size)}
    >
      <rect width="60" height="15" fill="#AA151B" />
      <rect y="15" width="60" height="30" fill="#F1BF00" />
      <rect y="45" width="60" height="15" fill="#AA151B" />
    </svg>
  );
}

function ItFlag({ size, title }: Props) {
  return (
    <svg
      viewBox="0 0 60 60"
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid slice"
      role={title ? "img" : "presentation"}
      aria-label={title}
      style={baseStyle(size)}
    >
      <rect width="20" height="60" fill="#008C45" />
      <rect x="20" width="20" height="60" fill="#F4F5F0" />
      <rect x="40" width="20" height="60" fill="#CD212A" />
    </svg>
  );
}

function DeFlag({ size, title }: Props) {
  return (
    <svg
      viewBox="0 0 60 60"
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid slice"
      role={title ? "img" : "presentation"}
      aria-label={title}
      style={baseStyle(size)}
    >
      <rect width="60" height="20" fill="#000000" />
      <rect y="20" width="60" height="20" fill="#DD0000" />
      <rect y="40" width="60" height="20" fill="#FFCE00" />
    </svg>
  );
}

function NlFlag({ size, title }: Props) {
  return (
    <svg
      viewBox="0 0 60 60"
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid slice"
      role={title ? "img" : "presentation"}
      aria-label={title}
      style={baseStyle(size)}
    >
      <rect width="60" height="20" fill="#AE1C28" />
      <rect y="20" width="60" height="20" fill="#FFFFFF" />
      <rect y="40" width="60" height="20" fill="#21468B" />
    </svg>
  );
}

const FLAGS: Record<FlagCode, (props: Props) => ReactElement> = {
  gb: GbFlag,
  fr: FrFlag,
  ma: MaFlag,
  es: EsFlag,
  it: ItFlag,
  de: DeFlag,
  nl: NlFlag,
};

export function FlagIcon({ code, size, title }: { code: FlagCode; size: number; title?: string }) {
  const Cmp = FLAGS[code];
  return <Cmp size={size} title={title} />;
}
